import type { DragBounds } from '@use-gesture/core/src/types/config';
import type { DragState } from '@use-gesture/core/src/types/state';
import { DragGesture } from '@use-gesture/vanilla';
import { DS_DRAGGING_BOUNDRY_ID, getTranslateXY } from '../helpers';

type Offset = { x: number; y: number };

interface DraggableOptions {
  handle?: string;
  cancel?: string;
}

export const dragOffsetDataKey = 'dragOffset';
export const defaultDraggableOptions: DraggableOptions = {
  handle: '.handle-dragging',
  cancel: '.prevent-dragging',
};

const shouldDrag = (el: HTMLElement, options: DraggableOptions) => {
  // if the handle selector is set and cancel selector is not set, check if the element matches the handle selector
  if (options.handle && !options.cancel) {
    return null !== el.closest(options.handle);
  }
  // if the handle selector is not set and cancel selector is set, check if the element matches the cancel selector
  if (options.cancel && !options.handle) {
    return null === el.closest(options.cancel);
  }
  // if both the handle and cancel selector are set, check if the element matches the handle selector
  // and if it doesn't match the cancel selector
  if (options.handle && options.cancel) {
    const dragHandle = el.closest(options.handle);
    const dragPrevent = el.closest(options.cancel);

    if (!dragHandle) {
      return false;
    }
    if (dragHandle && !dragPrevent) {
      return true;
    }
    if (dragPrevent && dragHandle) {
      return !dragHandle.contains(dragPrevent);
    }
  }
};

function toggleNaturalDraggingSideEffects(el: HTMLElement, enable: boolean) {
  const value = enable ? 'auto' : 'none';
  el.style.touchAction = value;
  el.style.userSelect = value;
}

export const getDragOffsetData = (el: HTMLElement): Offset => {
  const offsetData = el.dataset[dragOffsetDataKey];
  return offsetData ? JSON.parse(offsetData) : { x: 0, y: 0 };
};

export const updateDragOffsetData = (el: HTMLElement, add: Offset): void => {
  const previousOffset = getDragOffsetData(el);
  const newOffset: Offset = {
    x: Math.round(previousOffset.x + add.x),
    y: Math.round(previousOffset.y + add.y),
  };

  el.dataset[dragOffsetDataKey] = JSON.stringify(newOffset);
};

export function makeDraggable(el: HTMLElement, options?: DraggableOptions) {
  const initialOffset: Offset = { x: 0, y: 0 };

  const dragHandler = (state: DragState) => {
    const {
      target,
      movement: [mx, my],
      first,
      last,
    } = state;
    const clickedElement = target as HTMLElement;

    if (options && !shouldDrag(clickedElement, options)) {
      return;
    }

    if (first) {
      toggleNaturalDraggingSideEffects(clickedElement, false);
      const { x, y } = getTranslateXY(el);
      initialOffset.x = x;
      initialOffset.y = y;
    }

    el.style.transform = `translate3d(${initialOffset.x + mx}px, ${initialOffset.y + my}px, 0)`;

    if (last) {
      updateDragOffsetData(el, { x: mx, y: my });
      toggleNaturalDraggingSideEffects(clickedElement, true);
    }
  };

  const getDragBounds = (): DragBounds => {
    const boundaryElement = document.getElementById(DS_DRAGGING_BOUNDRY_ID);
    if (boundaryElement) {
      return boundaryElement;
    }

    // restrict drag to the boundaries of the body of the page
    if (document.body.clientHeight && document.body.clientWidth) {
      return document.body;
    }

    // if body doesn't have dimensions default to html element
    return document.documentElement;
  };

  return new DragGesture(el, dragHandler, {
    bounds: getDragBounds(),
  });
}
